/**
 * Asking about a running job again, without giving up on the first hiccup.
 *
 * Generation takes minutes, and over minutes a request will fail now and then
 * -- a dropped connection, a Lambda cold start past the gateway's timeout, a
 * 429. Polling used to stop on the first failure and never start again, so
 * the panel sat on "Generating" with an error underneath while the workpaper
 * finished on the server.
 */

/** How often to ask again while a job runs. Generation takes minutes. */
export const POLL_MS = 3000;

/** The longest wait between attempts while the server is not answering. */
export const MAX_BACKOFF_MS = 30_000;

/** Failures in a row before saying anything. One or two are noise. */
export const FAILURES_BEFORE_NOTICE = 3;

/**
 * When to stop asking about a generation that has not finished. The worker's
 * Lambda timeout is fifteen minutes, but an asynchronous invoke can wait in
 * Lambda's queue and be retried, so this leaves room for that.
 */
export const GENERATION_CAP_MS = 30 * 60 * 1000;

/**
 * When to stop asking about a GST review. The review worker times out at
 * fifteen minutes and is never retried, so twenty without an answer means it
 * stopped without recording why.
 */
export const REVIEW_CAP_MS = 20 * 60 * 1000;

/** Unfinished history rows polled at once, newest first. */
export const MAX_POLLED_ROWS = 5;

/**
 * Whether a failed poll is worth retrying.
 *
 * A network failure (fetch rejects with a TypeError), a server error, or
 * being rate limited: all say "not now" rather than "no". A 401, 403 or 404
 * will say the same thing on every attempt.
 *
 * Duck-typed on `status` rather than `instanceof ApiError`, so it holds for
 * any error carrying an HTTP status.
 */
export function isTransient(error: unknown): boolean {
  if (error instanceof TypeError) return true;
  const status = (error as { status?: unknown } | null)?.status;
  if (typeof status !== "number") return false;
  return status === 429 || status === 408 || status >= 500;
}

/** The wait before the next attempt: the normal interval, doubling per failure. */
export function retryDelay(failures: number): number {
  if (failures <= 0) return POLL_MS;
  return Math.min(POLL_MS * 2 ** failures, MAX_BACKOFF_MS);
}
